'use client';
import { Box, CircularProgress, keyframes } from '@mui/material';
import { useLinkStatus } from 'next/link';
import { MaterialIcon, type MaterialIconType } from '../materialIcon';

// Waits before swapping, so prefetched pages that open at once never flash the spinner
const SWAP_DELAY = 150;
const fadeOut = keyframes`to { opacity: 0; }`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const swapSx = (animation: string) => ({ animation: `${animation} 1ms linear ${SWAP_DELAY}ms both` });

type LinkPendingIconProps = {
  icon: MaterialIconType;
  size?: number;
  style?: React.CSSProperties;
  // Set by MUI's Tab on its icon: carries the gap to the label
  className?: string;
};

/**
 * Icon for a `next/link` that turns into a spinner while the link's page loads, so a tap on a slow page gets feedback
 * at once instead of leaving the old page on screen. Must render inside the Link.
 *
 * @param {MaterialIconType} icon - Icon shown while idle.
 * @param {number} size - Icon and spinner size. default: 24.
 * @param {React.CSSProperties} style - Styles passed to the icon, e.g. its font variation.
 * @return {React.JSX.Element} The rendered LinkPendingIcon component.
 */
export const LinkPendingIcon = ({ icon, size = 24, style, className }: LinkPendingIconProps) => {
  const { pending } = useLinkStatus();

  return (
    <Box
      component="span"
      className={className}
      aria-hidden="true"
      sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <MaterialIcon icon={icon} size={size} style={style} pointer sx={pending ? swapSx(fadeOut) : undefined} />
      {pending && (
        <CircularProgress
          size={size * 0.8}
          thickness={5}
          color="inherit"
          sx={{ position: 'absolute', ...swapSx(fadeIn) }}
        />
      )}
    </Box>
  );
};
